// Generic interactable dispatcher — registry-style replacement for the
// hand-wired branches in stage._updateHoverAtPointer + the key-event handler.
//
// Background: every interactable fixture historically owns a bespoke block
// in `_updateHoverAtPointer` (raycast, modal-scope claim, outline diff) and
// a matching `if (this.hovered*)` arm in the E-key handler. Adding a new
// interactable kind (Bailey command verbs, future Forge pack verbs) needs
// either a dedicated branch in both places or — what we're doing here — a
// real registry the dispatcher iterates without core edits.
//
// Each registered kind declares enough callbacks for the dispatcher to:
//   1. Decide whether to skip this frame entirely (`suppressedBy`).
//   2. Get the meshes it cares about (`getTargets`) + raycast against them.
//   3. Convert the raycast hit into "active state" the kind can act on
//      (`onHit`); null = no active state this frame.
//   4. Apply outline / state side-effects when active state transitions
//      (`commitHover`).
//   5. Handle E presses while active state is non-null (`interactE`).
//
// The dispatcher iterates kinds in registration order. Stage 1 migrates one
// fixture; Stage 2 will migrate the rest and modal-scope precedence will
// be honoured via per-kind `modal: true` flags returned from `onHit`. The
// registration order for Stage 1 doesn't matter (only one kind), so the
// precedence + modal-suppression machinery stays minimal here for now.

const registry = [];

// Register a new interactable kind. Each kind is a plain object with:
//   id: string                                  — short identifier ("longbox-lid")
//   precedence?: number                         — higher runs first; default 0
//   suppressedBy?: (stage) => boolean           — skip this kind entirely this frame
//   getTargets?: (stage) => Mesh[] | null       — meshes to raycast (null/empty = skip)
//   onHit?: (stage, mesh, hitInfo) => any|null  — convert raycast hit → active state
//   evaluateActive?: (stage) => any | null      — replaces getTargets+onHit when set.
//                                                 Use for non-raycast hit-tests OR
//                                                 when active state derives from a
//                                                 fixture's own internal state
//                                                 (e.g. wall-comic substituting the
//                                                 top sleeve of an active stack-table).
//   commitHover: (stage, prev, next) => void    — outline / state side-effects when
//                                                 active state changes (by reference)
//   interactE?: (stage, active) => boolean      — handle E; return true to consume
export function registerInteractable(kind) {
	if (!kind?.id) throw new Error("registerInteractable: kind.id required");
	if (registry.some((k) => k.id === kind.id)) {
		throw new Error(`registerInteractable: kind "${kind.id}" already registered`);
	}
	if (!kind.evaluateActive && !kind.getTargets) {
		throw new Error(`registerInteractable: kind "${kind.id}" must provide evaluateActive or getTargets`);
	}
	registry.push(kind);
	// Higher precedence first; stable for equal precedence (registration order).
	registry.sort((a, b) => (b.precedence ?? 0) - (a.precedence ?? 0));
}

// Per-stage dispatcher state lives on the stage instance under a single
// symbol-ish key so it doesn't collide with anything else.
const STATE_KEY = "_interactableDispatcherState";

function getState(stage) {
	if (!stage[STATE_KEY]) stage[STATE_KEY] = new Map();
	return stage[STATE_KEY];
}

// Run one dispatch pass — called from `_updateHoverAtPointer`. Each kind
// gets a chance to determine its active state via either `evaluateActive`
// or the raycast-based `getTargets + onHit` flow, gated by `suppressedBy`.
// Kinds are iterated in precedence order (highest first) so higher-priority
// modals have set their stage state before lower-priority kinds read it
// (via their `suppressedBy`).
//
// `commitHover` fires whenever the active-state reference changes (kinds
// with intra-active mutations like vinyl-bin's browseIdx should rebuild
// the active-state object each frame so commitHover sees the change).
export function runDispatcher(stage) {
	const state = getState(stage);
	for (const kind of registry) {
		const prev = state.get(kind.id) ?? null;
		let next = null;
		if (!(kind.suppressedBy && kind.suppressedBy(stage))) {
			if (kind.evaluateActive) {
				next = kind.evaluateActive(stage) ?? null;
			} else {
				const targets = kind.getTargets(stage);
				if (targets && targets.length > 0) {
					const hits = stage.raycaster.intersectObjects(targets, false);
					if (hits.length > 0) {
						next = kind.onHit(stage, hits[0].object, hits[0]) ?? null;
					}
				}
			}
		}
		if (prev !== next) kind.commitHover(stage, prev, next);
		state.set(kind.id, next);
	}
	// 🔴 ASK-162 — DEPTH BEFORE PRECEDENCE for a kind that can stand IN FRONT of the others (the companion). Precedence
	// decides between claims on the SAME spot; a dog standing before a wall of comics is not on the same spot, and a
	// finger on him opened the comic behind him (measured, probe-phone-walk PB, store-test-2). When such a kind's own hit
	// is the nearest thing along the ray, it takes the frame and every claim behind it yields.
	for (const kind of registry) {
		const d = kind.frontDistance?.(stage);
		if (d == null) continue;
		// The occlusion read is a whole-scene raycast: at most every 150 ms while the ray is on him (a phone frame is ~16 ms).
		const now = performance.now();   // a tap resets `_occAt` (stage.tapInteract): the finger's answer is always fresh
		if (!(now - (kind._occAt || 0) < 150)) { kind._occAt = now; const hit = stage.raycaster.intersectObjects(stage.scene.children, true).find((h) => h.object?.visible && !kind.owns(stage, h.object)); kind._front = !hit || hit.distance >= d; }
		if (!kind._front) continue;
		for (const k of registry) { const a = state.get(k.id); if (k !== kind && a) { k.commitHover(stage, a, null); state.set(k.id, null); } }
		const prev = state.get(kind.id) ?? null, next = kind.evaluateActive(stage) ?? null;
		if (prev !== next) kind.commitHover(stage, prev, next);
		state.set(kind.id, next);
	}
}

// Lookup helper for the E handler: walks the registry in registration order
// and gives the first kind whose active state is non-null a chance to handle
// the press. Returns true when a kind consumed it.
export function dispatchInteractE(stage) {
	const state = getState(stage);
	for (const kind of registry) {
		if (!kind.interactE) continue;
		const active = state.get(kind.id);
		if (!active) continue;
		if (kind.interactE(stage, active)) return true;
	}
	return false;
}

// Reset all per-stage dispatcher state. Called on scene tear-down so a
// stale active state doesn't leak across navigations.
export function resetDispatcher(stage) {
	const state = getState(stage);
	for (const kind of registry) {
		const prev = state.get(kind.id) ?? null;
		if (prev !== null) kind.commitHover(stage, prev, null);
	}
	state.clear();
}

/**
 * 🔴 ASK-137 §84 — THE HIGHEST-PRECEDENCE KIND WITH A LIVE ACTIVE STATE.
 *
 * This file's own header says what the registry is for: *"Adding a new interactable kind needs
 * either a dedicated branch in both places or — what we're doing here — a real registry the
 * dispatcher iterates without core edits."* Two places in `stage.js` kept their dedicated branches
 * anyway — `_hoveredProduct()` and `_updateEHint()`'s hover ladder — so every kind was enumerated by
 * hand, twice, and adding `seated-item` for F-115k meant adding a branch to both. That is the proof
 * the header predicted, and §76d named this as the payment for the two ceiling raises it caused.
 *
 * The dispatcher already keeps each kind's active state in PRECEDENCE order, which is exactly the
 * order those two ladders were written in by hand. So the ladders become one read, and each kind
 * carries its own prompt (`hoverHint`) and its own subject (`productOf`) — where the rest of its
 * behaviour already lives.
 */
export function activeInteractable(stage) {
	const state = getState(stage);
	for (const kind of registry) {
		const active = state.get(kind.id);
		if (active) return { kind, active };
	}
	return null;
}

/** The registered kinds, in precedence order — read by the stage for per-kind hero copy (§84). */
export function interactableKinds() { return registry; }

// Mostly for testing / debugging — not used at runtime.
export function _getRegistry() { return registry.slice(); }
